import { DestroyRef, Directive, effect, inject, input } from '@angular/core';
import { IonContent } from '@ionic/angular';

/* How far off the bottom the user may be before we stop following the stream. Below this
   they're still effectively at the bottom (a partially rendered line, a growing bubble). */
const STICKY_THRESHOLD_PX = 120;

/**
 * Keeps an `ion-content` pinned to the bottom of a conversation.
 *
 * Put it on the `ion-content` and feed it the message list:
 * `<ion-content [cChatAutoScroll]="messages()">`. Any new value re-triggers the follow -
 * and because a streaming bubble produces a new array on every chunk, the view tracks the
 * text as it grows, not just as messages are added.
 *
 * Scrolling up opts out until the user returns to the bottom, so reading back through the
 * conversation isn't yanked away by an answer still arriving. Call `follow()` to pin the
 * view again (sending a message should), and `release()` to let go of it (clearing the
 * conversation should, so the reset lands at the top).
 *
 * Shrinking the viewport re-pins too, so the keyboard opening doesn't push the newest
 * message out of sight.
 */
@Directive({
  selector: '[cChatAutoScroll]',
})
export class ChatAutoScrollDirective {
  /* The value is never read - it exists so any change schedules a scroll. */
  readonly cChatAutoScroll = input<unknown>();

  private readonly content = inject(IonContent);
  private readonly destroyRef = inject(DestroyRef);

  private frame: number | null = null;
  private scrollElement: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private destroyed = false;

  /* Whether we're still following the conversation. Only the user's own scrolling flips
     this - never a content change. Measuring at the moment new content lands would compare
     against a conversation that has already grown, and one sent message plus the typing
     indicator is taller than the threshold on its own, so following would stop the first
     time anything was sent. */
  private sticky = true;

  constructor() {
    void this.watchScrollElement();

    effect(() => {
      this.cChatAutoScroll();
      this.scheduleScroll();
    });

    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.scrollElement?.removeEventListener('scroll', this.onUserScroll);
      this.resizeObserver?.disconnect();
    });
  }

  /** Pins the view to the bottom again, wherever the user left it. */
  follow(): void {
    this.sticky = true;
    this.scheduleScroll();
  }

  /** Lets go of the bottom until the user scrolls back down to it. */
  release(): void {
    this.sticky = false;
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  private async watchScrollElement(): Promise<void> {
    const element = await this.content.getScrollElement();
    if (this.destroyed) return;

    this.scrollElement = element;
    /* Deliberately a plain listener rather than `NgZone.runOutsideAngular`: as long as
       zone.js is around this costs a change detection pass per scroll event even though
       the handler only sets a private flag, but the app is moving to zoneless and new code
       shouldn't gain zone references it would only have to shed again. */
    element.addEventListener('scroll', this.onUserScroll, { passive: true });

    /* The keyboard opening shortens the scroll element without touching `scrollTop`, so the
       bottom of the conversation slides under the keyboard and not a single scroll event
       fires to notice it. Watching the box itself also covers the composer growing to a
       second line and a rotation - and unlike Keyboard.addListener it needs no plugin, no
       animation-duration guess, and works on the web too. */
    this.resizeObserver = new ResizeObserver(() => this.scheduleScroll());
    this.resizeObserver.observe(element);
  }

  private readonly onUserScroll = (): void => {
    const element = this.scrollElement;
    if (!element) return;

    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    this.sticky = distanceFromBottom <= STICKY_THRESHOLD_PX;
  };

  private scheduleScroll(): void {
    if (this.frame !== null) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      if (!this.sticky) return;

      void this.content.scrollToBottom(0);
    });
  }
}
